import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import Body from "../../commonComponents/body/Body";
import ReactTable from "../../commonComponents/reactTable/ReactTable";
import { generateReportHeaders } from "../../utils/table.utils";
import { exportTableDetails, getAllData, getHeaders } from "../../services/common.service";
import { Button, Divider, Group, Text } from "@mantine/core";
import HeaderWithSearch from "../../commonComponents/components/headersWithSearch/HeaderWithSearch";
import { useLocation, useNavigate } from "react-router-dom";
import { COLORS } from "../../constants/colors";
import { IconArrowLeft, IconDownload, IconPlus } from "@tabler/icons-react";
import CrewMovementSheet from "./CrewMovementSheetDrawer";
import InflightSheet from "./InflightSheetDrawer";
import exportData from "../../utils/exportdata";
import { displayNotification } from "../../commonComponents/notifications/displayNotification";
import { downloadPDF, exportPDFRaw } from "../../services/apiCall/downloadpdfAPI";
import DrawerFormComp from "../../commonComponents/components/drawer/DrawerFormComp";
import useAuthStore from "../../store/authStore";

const FormsPage = () => {
  const { state = {} } = useLocation();
  const formName = state?.report_name;
  const source = state?.api;
  const { auth } = useAuthStore()
  const [cmsEditVisible , setCmsEditVisible] = useState(false)

  const role = auth?.emp_role_id?.role_name?.toLowerCase();
  const empCode = auth?.emp_code;

  const [pagination, setPagination] = useState({
    page: 1,
    search: "",
    source,
  });

  useEffect(() => {
    if (role == "scc" || role == "cc") {
      if (source === "crew_movement_report") {
        setPagination(prev => ({
          ...prev,
          filter: { emp_id: empCode }
        }));
      }

      if (source === "inflight_report") {
        setPagination(prev => ({
          ...prev,
          filter: {
            or: [
              { c1_staff_id_eq: empCode },
              { c2_staff_id_eq: empCode }
            ]
          }
        }));
      }
    }
  }, [role, empCode, source]);

  const exportDataMutation = useMutation({
    mutationKey: 'export-data-' + source,
    mutationFn: (filtersForExport) => exportTableDetails(filtersForExport),
    onSuccess: (res) => {
      const fileUrl = res?.data?.[0]?.url;
      exportData(fileUrl);
    },
    onError: (err) => {
      console.err(err);
      displayNotification({
        message: 'File Download Failed',
        variant: 'error',
      });
    },
  });

  const navigate = useNavigate();
  const [drawerData, setDrawerData] = useState({
    modal: false,
    mode: "",
    data: {},
    recordId: null, // 👈 Added field to store clicked row ID
  });

  // ✅ Fetch all records
  const allRecordsQuery = useQuery({
    queryKey: ["all-records", source, pagination],
    queryFn: () => getAllData(pagination),
  });

  // ✅ Fetch table headers dynamically
  const tableHeadersQuery = useQuery({
    queryKey: ["table-headers", source],
    queryFn: () => getHeaders({ source }),
    select: (data) => ({
      headers: generateReportHeaders(data?.[0]),
      data: data?.[0],
    }),
  });

  useEffect(() => {
    if (pagination?.search) {
      setPagination((old) => ({ ...old, page: 1 }));
    }
  }, [pagination?.search]);

  const handleSorting = (e) => {
    const d = e(pagination.sort);
    setPagination((old) => ({ ...old, sort: d }));
  };

  // ✅ Render drawer content dynamically and pass recordId
  const renderDrawerContent = () => {
    const commonProps = {
      mode: drawerData.mode,
      recordId: drawerData.recordId,
      onClose: () => setDrawerData({ modal: false, mode: "", recordId: null }),
    };

    switch (source) {
      case "crew_movement_report":
        return <CrewMovementSheet {...commonProps} showEdit={setCmsEditVisible} />;
      case "inflight_report":
        return <InflightSheet {...commonProps}/>;
      default:
        return null;
    }
  };

  return (
    <Body>
      {/* Header */}
      <Group justify="space-between">
        <Group gap={3}>
          <IconArrowLeft
            cursor="pointer"
            size={24}
            color="gray"
            onClick={() => navigate("/forms")}
          />
          <HeaderWithSearch
            title={formName}
            pagination={pagination}
            setPagination={setPagination}
          />
        </Group>

        <Group>
          {role !== "scc" && role !== "cc" && (
            <Button
              size="xs"
              variant="outline"
              color={COLORS.green}
              onClick={() => {
                const filtersForExport = { source, ...pagination };
                exportDataMutation.mutate(filtersForExport);
              }}
              rightSection={<IconDownload size={16} />}
            >
              Export
            </Button>
          )}

          <Button
            size="xs"
            variant="filled"
            color={COLORS.green}
            rightSection={<IconPlus size={16} />}
            onClick={() =>
              setDrawerData({
                modal: true,
                mode: "add",
                data: {},
                recordId: null,
              })
            }
          >
            Add
          </Button>
        </Group>
      </Group>

      {/* Table */}
      <ReactTable
        sorting={pagination.sort}
        setSorting={handleSorting}
        columnData={tableHeadersQuery?.data?.headers}
        loading={allRecordsQuery?.isFetching || tableHeadersQuery?.isLoading}
        totalNoOfPages={allRecordsQuery?.data?.metadata?.total_number_of_page}
        totalNoOfRecords={allRecordsQuery?.data?.metadata?.records}
        rowData={allRecordsQuery?.data?.data}
        page={pagination?.page}
        setPage={(page) => setPagination({ ...pagination, page })}
        onRowClick={(row) => {
          // 👇 Send row data & ID to drawer
          setDrawerData({
            modal: true,
            mode: "view",
            recordId: row.id, // ✅ Pass ID here
          });
        }}
      />

      {/* Drawer */}
      <DrawerFormComp
        opened={drawerData.modal}
        onClose={() =>
          setDrawerData({ modal: false, mode: "", data: {}, recordId: null })
        }
        size="xl"
        title={
          <div style={{ width: "100%" }}>
            <Group
              w="100%"
              position="apart"
              justify="space-between"
              align="center"
            >
              <Text>
                {drawerData.mode === "add"
                  ? `Add ${formName}`
                  : drawerData.mode === "view"
                    ? `View ${formName}`
                    : `Edit ${formName}`}
              </Text>
              <Group gap={10}>
                {drawerData.mode === "view" && (
                  <>
                    {role != "scc" && role != "cc" && (
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={async () => {
                          try {
                            const endpoint =
                              source === "inflight_report"
                                ? `generate-inflight-report/${drawerData.recordId}`
                                : `crew-movement-report/${drawerData.recordId}`;

                            const pdfBytes = await exportPDFRaw(endpoint, {});
                            downloadPDF(
                              pdfBytes,
                              `${formName}_${drawerData.recordId}.pdf`
                            );
                          } catch (err) {
                            displayNotification({
                              message: "File Download Failed",
                              variant: "error",
                            });
                          }
                        }}
                      >
                        Download
                      </Button>
                    )}

                    {(cmsEditVisible && source === "crew_movement_report") && (
                      <Button
                        size="xs"
                        variant="filled"
                        mr={10}
                        onClick={() => {
                          setDrawerData((old) => ({
                            ...old,
                            mode: "edit",
                          }));
                        }}
                      >
                        Edit
                      </Button>
                    )}

                    {(role !== "scc" && role !== "cc" && source === "inflight_report") && (
                      <Button
                        size="xs"
                        variant="filled"
                        mr={10}
                        onClick={() => {
                          setDrawerData((old) => ({
                            ...old,
                            mode: "edit",
                          }));
                        }}
                      >
                        Edit
                      </Button>
                    )}
                  </>
                )}
              </Group>
            </Group>
            <Divider my={"sm"} />
          </div>
        }
      >
        {renderDrawerContent()}
      </DrawerFormComp>
    </Body>
  );
};

export default FormsPage;
